import { toast } from "sonner";

import axiosInstance from "./axiosConfig";
import type { GenericResponse, TErrorResponse } from "./generalTypes";

export interface IUpload {
  message: string;
  status: string;
  status_code: number;
  payload: string;
}

export const errorToast = (message = "Something went wrong") => {
  toast.error(message);
};

export const successToast = (message = "Successful") => {
  toast.success(message);
};

export const saveLocalStorage = (data: unknown, key: string) => {
  try {
    const jsonData = JSON.stringify(data);
    localStorage.setItem(key, jsonData);
    return true;
  } catch (error) {

    return false;
  }
};

export const getLocalStorage = (key: string) => {
  try {
    const jsonData = localStorage.getItem(key);
    if (!jsonData) return null;
    return JSON.parse(jsonData) as unknown;
  } catch (error) {

    return null;
  }
};

export const clearLocalStorage = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {

    return null;
  }
  return null;
};

export const getQueryKeys = (namespace: string) => ({
  create: `${namespace}/create`,
  createOne: `${namespace}/createOne`,
  read: `${namespace}/read`,
  readOne: `${namespace}/readOne`,
  update: `${namespace}/update`,
  patch: `${namespace}/patch`,
  put: `${namespace}/put`,
  delete: `${namespace}/delete`,
});

export function hasTokenExpired(isoTimestamp: string): boolean {
  const currentDateTime = new Date();
  const expirationDateTime = new Date(isoTimestamp);

  // Compare the current date/time to the expiration date/time
  return currentDateTime >= expirationDateTime;
}

export async function uploadFile(body: unknown): Promise<IUpload> {
  const res = await axiosInstance.post(`/uploads/admins`, body, {
    headers: {
      "Content-Type": "multipart/form-data",
      "X-Request-Source": "med-connect",
    },
  });

  return res.data;
}

export const getParams = <T>(param: T): { [K in keyof T]: string } => {
  const result: { [K in keyof T]: string } = {} as { [K in keyof T]: string };
  for (const key in param) {
    if (Object.prototype.hasOwnProperty.call(param, key)) {
      const value = param[key];
      result[key] = value !== undefined && value !== null ? String(value) : "";
    }
  }
  return result;
};

export function constructUrl(
  baseUrl: string,
  params: Record<string, string | boolean | undefined | null>
): string {
  let url = `${baseUrl}?`;
  const queryParams = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== "")
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    )
    .join("&");

  url += queryParams;
  return url.endsWith("?") ? url.slice(0, -1) : url;
}

// Converts a GenericResponse to TErrorResponse
export function genericToTErrorResponse(err: GenericResponse<unknown>): TErrorResponse {
  return {
    code: String(err.statusCode ?? 500),
    error: true,
    message: err.message ?? 'Unknown error',
    // details: undefined // Optionally map details if needed
  };
}

// Safely handle unknown errors and convert to TErrorResponse or fallback
export function safeHandleGenericError(err: unknown, handle: (err: TErrorResponse) => string): string {
  if (err && typeof err === 'object' && 'statusCode' in err && 'message' in err) {
    return handle(genericToTErrorResponse(err as GenericResponse<unknown>));
  }
  return 'An unknown error occurred.';
}

export function handleErrors(err: TErrorResponse) {
  return err.message || "Something went wrong";
}
